/**
 * Was beim Ein- und Ausbuchen zur Auswahl steht (ADR 0020).
 *
 * - **Ausbuchen:** der Inhalt des Behälters -- Artikel und die Packstücke
 *   darin, die als Ganzes mitgehen. In „Unbekannt“ auch das Verlorene: Wer es
 *   von dort ausbucht, hat es wiedergefunden.
 * - **Einbuchen:** was hierher gehört, aber gerade woanders ist -- Stammplatz
 *   hier (so vom Nutzer entschieden). Verliehenes nicht: Das kommt über die
 *   Rücknahme der Ausleihe zurück, wie bei „Alles da“ (ADR 0013 §8).
 *   Verlorenes auch nicht.
 *
 * Alles andere kommt über den Scanner dazu. Aus der lokalen Kopie, also auch
 * ohne Netz.
 */

import type { Artikel, Lagerort, Zustand } from "@thw/contracts";
import type { Bestand } from "./bestand.js";
import { ortDesArtikels } from "./verschieben.js";

export interface AuswahlZeile {
  artikel: Artikel;
  zustand: Zustand;
  /** Der Lagerort, der dieser Artikel ist -- bei einem Packstück. */
  packstueck: Lagerort | null;
}

function zeile(bestand: Bestand, artikel: Artikel, zustand: Zustand): AuswahlZeile {
  return { artikel, zustand, packstueck: ortDesArtikels(bestand, artikel.id) };
}

/** Erst die Artikel nach Bezeichnung und Nummer, die Packstücke zuletzt. */
function sortieren(liste: AuswahlZeile[]): AuswahlZeile[] {
  return liste.sort(
    (a, b) =>
      Number(a.packstueck !== null) - Number(b.packstueck !== null) ||
      a.artikel.bezeichnung.localeCompare(b.artikel.bezeichnung, "de") ||
      a.artikel.inventarnummer.localeCompare(b.artikel.inventarnummer, "de", { numeric: true }),
  );
}

/** Der Behälter selbst ist nie dabei -- er geht über „Verschieben“. */
function eigenerArtikel(bestand: Bestand, lagerort_id: string): string | null {
  return bestand.lagerorte.get(lagerort_id)?.artikel_id ?? null;
}

export function ausbuchenAuswahl(bestand: Bestand, lagerort_id: string): AuswahlZeile[] {
  const selbst = eigenerArtikel(bestand, lagerort_id);
  const unbekannt = bestand.lagerorte.get(lagerort_id)?.unbekannt === true;
  const liste: AuswahlZeile[] = [];
  for (const [artikel_id, z] of bestand.zustaende) {
    if (z.lagerort_id !== lagerort_id || artikel_id === selbst) continue;
    if (z.archiviert && !unbekannt) continue;
    const a = bestand.artikel.get(artikel_id);
    if (a !== undefined) liste.push(zeile(bestand, a, z));
  }
  return sortieren(liste);
}

export function einbuchenAuswahl(bestand: Bestand, lagerort_id: string): AuswahlZeile[] {
  const selbst = eigenerArtikel(bestand, lagerort_id);
  const liste: AuswahlZeile[] = [];
  for (const a of bestand.artikel.values()) {
    if ((a.stammplatz_id ?? null) !== lagerort_id || a.id === selbst) continue;
    const z = bestand.zustaende.get(a.id);
    if (z === undefined || z.lagerort_id === lagerort_id) continue;
    if (z.archiviert || z.verbleib === "ausgeliehen") continue;
    liste.push(zeile(bestand, a, z));
  }
  return sortieren(liste);
}
